import { groq } from 'next-sanity';
import { z } from 'zod';

import { SanityBasicText } from './basicText';
import { documentFootnotes, SanityDocumentFootnotes } from './footnotes';
import { image, SanityImage } from './image';
import { relatedContent, SanityRelatedContent } from './relatedContent';
import { richText } from './richText';

export const SanityBlogPostOrCaseStudyBase = z.object({
  _id: z.string(),
  _updatedAt: z.string(),
  _key: z.string().optional().nullable(),
  title: z.string(),
  href: z.string(),
  publishedAt: z.string(),
  thumbnailImage: SanityImage,
  excerpt: z.string(),
  content: SanityBasicText,
  author: z.array(
    z.object({
      _key: z.string().nullable(),
      name: z.string(),
      href: z.string(),
      role: z.string().optional().nullable(),
      avatar: SanityImage,
    }),
  ),
  category: z.array(
    z.object({
      title: z.string(),
      href: z.string(),
    }),
  ),
  relatedContent: SanityRelatedContent.nullable(),
  footnotes: SanityDocumentFootnotes.optional().nullable(),
});

export const SanityBlogPost = SanityBlogPostOrCaseStudyBase.extend({
  readTime: z.number(),
});

export const SanityCaseStudy = SanityBlogPostOrCaseStudyBase.extend({
  // TODO: Once migrated remove the optional and nullable
  companyIntro: z
    .object({
      companyName: z.string().optional().nullable(),
      companyLogo: SanityImage.optional().nullable(),
      companyLogoWidth: z.number().optional().nullable(),
      companyLogoMobileWidth: z.number().optional().nullable(),
      description: z.string(),
      useCase: z.string(),
      industry: z.string(),
    })
    .nullable()
    .optional(),
  stats: z
    .array(
      z.object({
        _key: z.string().optional().nullable(),
        label: z.string(),
        value: z.string(),
        caption: z.string(),
      }),
    )
    .optional()
    .nullable(),
  testimonial: z
    .object({
      quote: z.string(),
      name: z.string(),
      caption: z.string(),
      headshot: SanityImage.optional().nullable(),
    })
    .optional()
    .nullable(),
});

export type SanityBlogPost = z.infer<typeof SanityBlogPost>;

export type SanityCaseStudy = z.infer<typeof SanityCaseStudy>;

export type SanityBlogPostOrCaseStudy = SanityBlogPost | SanityCaseStudy;

// Shared query function for blog post and case study post pages
function createContentPostQuery(
  contentType: 'post' | 'caseStudy',
  basePath: string,
  categoryPath: string,
) {
  return groq`*[_type == '${contentType}' && slug.current == $slug][0] {
    _id,
    _updatedAt,
    title,
    'href': '${basePath}' + slug.current,
    publishedAt,
    author[]-> {
      _key,
      name,
      role,
      'href': '/blog/author/' + slug.current,
      avatar ${image},
    },
    category[]-> {
      _key,
      title,
      'href': '${categoryPath}' + slug.current,
    },
    readTime,
    thumbnailImage ${image},
    excerpt,
    content[] ${richText},
    relatedContent ${relatedContent},
    footnotes ${documentFootnotes},
    companyIntro {
      companyName,
      companyLogo ${image},
      companyLogoWidth,
      companyLogoMobileWidth,
      description,
      useCase,
      industry,
    },
    stats[] {
      _key,
      label,
      value,
      caption,
    },
    testimonial {
      quote,
      name,
      caption,
      headshot ${image},
    },
  }`;
}

export const blogPost = createContentPostQuery(
  'post',
  '/blog/',
  '/blog/categories/',
);

export const caseStudy = createContentPostQuery(
  'caseStudy',
  '/case-studies/',
  '/case-studies/categories/',
);
